import {
  Box,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Text,
  XAxis,
  YAxis,
} from 'recharts';

import { Enum } from '~/data/enums';
import { formatCurrency, formatNumber } from '~/services/Formatters';
import { People } from '~/services/Http';

import { useSxStyles } from './useStyles';

export interface Props {
  compensationSummary: People.CompensationsSummaryDto;
  getSalaryFrequency: (code?: string) => Enum | undefined;
}

export const MyCompensationDialogContent: React.FC<Props> = ({
  compensationSummary,
  getSalaryFrequency,
}) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const { current, history, latestPaidIncentives } = compensationSummary;

  const latestCompensationCurrency = formatCurrency(current.currency);

  return (
    <>
      <DialogTitle sx={sxStyles.dialogTitleRoot} variant="subtitle2">
        {t('myCompensation')}
      </DialogTitle>
      <DialogContent sx={sxStyles.dialogContentRoot}>
        <Box sx={sxStyles.miniTableRow}>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={sxStyles.miniTableAttribute}
          >
            {t('baseSalary')}
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={sxStyles.miniTableAttribute}
          >
            {t('frequency')}
          </Typography>
        </Box>
        <Divider sx={sxStyles.divider} />
        <Box sx={sxStyles.miniTableRow}>
          <Typography variant="body2" sx={sxStyles.miniTableValue}>
            {`${formatNumber(
              current.baseSalary,
              2,
            )}${latestCompensationCurrency}`}
          </Typography>
          <Typography sx={sxStyles.miniTableValue} data-cy="frequency">
            {getSalaryFrequency(current.salaryFrequency)?.name}
          </Typography>
        </Box>

        <ResponsiveContainer width="100%" height={135}>
          <LineChart
            data={history.map((c) => ({
              baseSalary: c.baseSalary,
              startDate: moment(c.startDate).valueOf(),
            }))}
            margin={{
              top: 16,
              bottom: 16,
              right: 4,
            }}
          >
            <YAxis
              dataKey="baseSalary"
              domain={['dataMin', 'auto']}
              tick={(props) => (
                <Text {...props} fontSize="0.75rem">
                  {props.payload.value}
                </Text>
              )}
              tickLine={false}
              tickCount={4}
              interval={0}
              width={36}
            />
            <XAxis
              hide={true}
              type="number"
              dataKey="startDate"
              domain={['dataMin', 'dataMax']}
              tickCount={4}
              interval={0}
            />
            <CartesianGrid stroke="#eee" />
            <Line type="linear" dataKey="baseSalary" stroke="#FFD78F" />
          </LineChart>
        </ResponsiveContainer>

        {latestPaidIncentives.length ? (
          <>
            <Typography variant="caption" gutterBottom={true}>
              {t('latestPaidIncentives').toUpperCase()}
            </Typography>

            <Box sx={sxStyles.incentives}>
              <Box sx={sxStyles.miniTableRow}>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={sxStyles.miniTableAttribute}
                >
                  {t('incentive')}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{
                    ...sxStyles.miniTableAttribute,
                    ...sxStyles.middlePositionElement,
                  }}
                >
                  {t('earning')}
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={sxStyles.miniTableAttribute}
                >
                  {t('date')}
                </Typography>
              </Box>
              <Divider sx={sxStyles.divider} />
              {latestPaidIncentives.map((latestPaidIncentive) => (
                <Box sx={sxStyles.miniTableRow} key={latestPaidIncentive.id}>
                  <Typography variant="body2" sx={sxStyles.miniTableValue}>
                    {latestPaidIncentive.incentiveType.value}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      ...sxStyles.miniTableValue,
                      ...sxStyles.middlePositionElement,
                    }}
                  >
                    {`${latestPaidIncentive.paymentAmount.toFixed(
                      2,
                    )}${latestCompensationCurrency}`}
                  </Typography>
                  <Typography variant="body2" sx={sxStyles.miniTableValue}>
                    {moment(latestPaidIncentive.paymentDate).format('L')}
                  </Typography>
                </Box>
              ))}
            </Box>
          </>
        ) : null}
      </DialogContent>
    </>
  );
};
